import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProgressLine } from '@/components/WindCharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

// Metric definitions (keys match score_history fields). Solid, distinct line colors.
const METRICS = [
  { key: 'overall', color: '#00B4D8', i18n: 'charts.overall' },
  { key: 'body_position', color: '#48CAE4', i18n: 'charts.body' },
  { key: 'kite_control', color: '#2EE59D', i18n: 'charts.kite' },
  { key: 'board_edge', color: '#FFB020', i18n: 'charts.edge' },
  { key: 'timing', color: '#FF6B35', i18n: 'charts.timing' },
  { key: 'style', color: '#E0457B', i18n: 'charts.style' },
];

const SKILLS = METRICS.filter((m) => m.key !== 'overall');

const firstLast = (history, key) => {
  const vals = history.map((h) => h[key]).filter((v) => v != null);
  if (!vals.length) return { current: null, delta: 0 };
  return { current: vals[vals.length - 1], delta: +(vals[vals.length - 1] - vals[0]).toFixed(1) };
};

const TrendCard = ({ label, color, current, delta }) => {
  const up = delta > 0.05, down = delta < -0.05;
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const trendColor = up ? '#2EE59D' : down ? '#FF6B35' : '#B9C4CF';
  return (
    <div className="rounded-xl bg-[#0A0A0A] border border-white/8 p-3" data-testid={`trend-card`}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground truncate">{label}</span>
        <span className="flex items-center gap-0.5 text-xs font-data" style={{ color: trendColor }}>
          <Icon className="w-3 h-3" />{delta > 0 ? '+' : ''}{delta}
        </span>
      </div>
      <div className="font-data text-2xl font-semibold mt-1" style={{ color }}>
        {current != null ? current.toFixed(1) : '—'}<span className="text-sm text-muted-foreground">/10</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/8 mt-2 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${(current || 0) * 10}%`, backgroundColor: color }} />
      </div>
    </div>
  );
};

export const SkillTrends = ({ history = [] }) => {
  const { t } = useTranslation();
  const [metric, setMetric] = useState('overall');
  const active = METRICS.find((m) => m.key === metric) || METRICS[0];

  const skillTrends = useMemo(
    () => SKILLS.map((s) => ({ ...s, ...firstLast(history, s.key) })),
    [history]
  );

  const enough = history.length >= 2;

  return (
    <div className="kite-card p-5" data-testid="skill-trends">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-semibold">{t('dashboard.skillTrendsTitle')}</h3>
          <p className="text-sm text-muted-foreground">{t('dashboard.skillTrendsSub')}</p>
        </div>
        {/* metric selector */}
        <div className="flex flex-wrap gap-1.5" role="tablist" aria-label={t('dashboard.skillTrendsTitle')}>
          {METRICS.map((m) => {
            const sel = m.key === metric;
            return (
              <button
                key={m.key}
                role="tab"
                aria-selected={sel}
                onClick={() => setMetric(m.key)}
                data-testid={`trend-metric-${m.key}`}
                className={`text-xs font-medium px-2.5 py-1 rounded-lg border transition-colors ${sel ? 'text-black' : 'text-muted-foreground border-white/12 hover:bg-white/5'}`}
                style={sel ? { backgroundColor: m.color, borderColor: m.color } : undefined}
              >
                {t(m.i18n)}
              </button>
            );
          })}
        </div>
      </div>

      {enough ? (
        <ProgressLine data={history} metric={active.key} color={active.color} />
      ) : (
        <div className="py-10 text-center text-sm text-muted-foreground" data-testid="trends-need-more">
          {t('dashboard.trendsNeedMore')}
        </div>
      )}

      {/* per-skill trend cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
        {skillTrends.map((s) => (
          <TrendCard key={s.key} label={t(s.i18n)} color={s.color} current={s.current} delta={s.delta} />
        ))}
      </div>
    </div>
  );
};

export default SkillTrends;
